import {useEffect, useMemo} from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetScheduledMessages as GetScheduledMessagesQuery,
    GetScheduledMessagesVariables as GetScheduledMessagesQueryVariables,
    InboxChannelEventFragment,
} from 'nutshell-graphql-types';

import GET_SCHEDULED_MESSAGES from '../queries/get-scheduled-messages.graphql';

const POLL_INTERVAL_MILLISECONDS = 5000; // 5 seconds

export const useGetScheduledMessages = (
    channelId?: string | null,
    threadId?: string | null
): {
    scheduledMessages: any[];
    isLoading: boolean;
    refetch: () => Promise<any>;
} => {
    const {
        data,
        error,
        loading: isLoading,
        refetch,
        startPolling,
        stopPolling,
    } = useQuery<GetScheduledMessagesQuery, GetScheduledMessagesQueryVariables>(
        GET_SCHEDULED_MESSAGES,
        {
            skip: !channelId,
            // @ts-expect-error
            variables: {channelId, threadId},
        }
    );

    const scheduledMessages = data ? data.scheduledMessages : null;

    const messages: InboxChannelEventFragment[] = useMemo(
        () => (scheduledMessages ? scheduledMessages.edges.map((edge) => edge.node) : []),
        [scheduledMessages]
    );

    useEffect(() => {
        if (messages && channelId) {
            startPolling(POLL_INTERVAL_MILLISECONDS);
        }

        return () => stopPolling();
    }, [messages, startPolling, stopPolling, channelId]);

    return {
        scheduledMessages: messages,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        refetch,
    };
};
